// Hooks TanStack Query da administração de identidade (Parte 3.6; GIY-037), sobre o cliente tipado gerado do contrato.
// Chaves começam pelo escopo (`['identity', 'network', …]` / `['identity', 'tenant', tenantId, …]`) para o clear de cache
// na troca de contexto (regra 6 de 14.7) manter tudo coerente. Mutações são `silent`: cada tela mostra o erro inline.
import type { components } from '@giyus/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';
import { useAuth } from '../../../lib/auth/authContext';
import { isStepUpRequired } from '../../../lib/auth/useStepUp';
import {
  buildDirectoryFallbackOutcome,
  isActiveGiyusDirectoryInput,
  mergeWithPersistedUsers,
  persistDirectoryUserSummary,
} from './directoryUsersPersistence';
import type { IdentityScope } from './scope';

export type UserSummary = components['schemas']['IdentityUserSummaryResponse'];
export type UserDetail = components['schemas']['IdentityUserDetailResponse'];
export type UserInvitation = components['schemas']['IdentityUserInvitationResponse'];
export type UserLock = components['schemas']['IdentityUserLockResponse'];
export type TemporaryAccount = components['schemas']['IdentityTemporaryAccountResponse'];
export type MfaResetRequest = components['schemas']['IdentityMfaResetRequestResponse'];
export type OidcClient = components['schemas']['IdentityOidcClientResponse'];
export type OidcClientSecret = components['schemas']['IdentityOidcClientSecretResponse'];
export type ChangeReasonInput = components['schemas']['IdentityChangeReasonRequest'];
export type CreateUserInput = components['schemas']['IdentityCreateUserRequest'];
export type CreateTemporaryAccountInput =
  components['schemas']['IdentityCreateTemporaryAccountRequest'];
export type ExtendTemporaryAccountInput =
  components['schemas']['IdentityExtendTemporaryAccountRequest'];
export type CreateOidcClientInput = components['schemas']['IdentityCreateOidcClientRequest'];
export type UpdateOidcClientInput = components['schemas']['IdentityUpdateOidcClientRequest'];
export type UserStatus = components['schemas']['UserStatus'];
export type UserKind = components['schemas']['UserKind'];

export const USER_STATUSES = ['Invited', 'Active', 'Locked', 'Disabled', 'Expired'] as const;
export const USER_KINDS = ['Human', 'Service', 'TemporaryHuman'] as const;

export interface UserListFilters {
  readonly status?: UserStatus | undefined;
  readonly kind?: UserKind | undefined;
}

export function scopeKey(scope: IdentityScope): readonly string[] {
  return scope.kind === 'network'
    ? ['identity', 'network']
    : ['identity', 'tenant', scope.tenantId];
}

/** Resposta do convite: `201` usuário novo, `200` usuário existente (REQ-AUTH-020). */
export interface InviteOutcome {
  readonly created: boolean;
  readonly result: UserInvitation;
}

export function useIdentityUsers(scope: IdentityScope, filters: UserListFilters) {
  const client = useApiClient();
  const query = {
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
  };
  return useQuery({
    queryKey: [...scopeKey(scope), 'users', filters],
    queryFn: async (): Promise<UserSummary[]> => {
      if (scope.kind === 'network') {
        const { data } = await client.GET('/api/v1/network/identity/users', { params: { query } });
        return mergeWithPersistedUsers(data ?? [], filters);
      }
      const { data } = await client.GET('/api/v1/tenants/{tenantId}/identity/users', {
        params: { path: { tenantId: scope.tenantId }, query },
      });
      return mergeWithPersistedUsers(data ?? [], filters);
    },
    meta: { silent: true },
  });
}

/** Detalhe: rede traz sessões e fatores; tenant só a projeção mínima + convite pendente (REQ-AUTH-020). */
export interface UserDetailView {
  readonly user: UserSummary;
  readonly sessions: UserDetail['sessions'] | null;
  readonly mfa: UserDetail['mfa'] | null;
  readonly pendingInvitation: UserDetail['pendingInvitation'];
}

export function useIdentityUser(scope: IdentityScope, id: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...scopeKey(scope), 'users', id],
    queryFn: async (): Promise<UserDetailView> => {
      if (scope.kind === 'network') {
        const { data } = await client.GET('/api/v1/network/identity/users/{id}', {
          params: { path: { id } },
        });
        if (!data) {
          throw new Error('Empty response');
        }
        return {
          user: data.user,
          sessions: data.sessions,
          mfa: data.mfa,
          pendingInvitation: data.pendingInvitation,
        };
      }
      const { data } = await client.GET('/api/v1/tenants/{tenantId}/identity/users/{id}', {
        params: { path: { tenantId: scope.tenantId, id } },
      });
      if (!data) {
        throw new Error('Empty response');
      }
      return { user: data.user, sessions: null, mfa: null, pendingInvitation: data.invitation };
    },
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

function useInvalidateScope(scope: IdentityScope) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: scopeKey(scope) });
  };
}

export function useInviteUser(scope: IdentityScope) {
  const client = useApiClient();
  const { backend } = useAuth();
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: CreateUserInput): Promise<InviteOutcome> => {
      const postUser = async () =>
        scope.kind === 'network'
          ? await client.POST('/api/v1/network/identity/users', { body: input })
          : await client.POST('/api/v1/tenants/{tenantId}/identity/users', {
              params: { path: { tenantId: scope.tenantId } },
              body: input,
            });
      const isEntraUser = isActiveGiyusDirectoryInput(input);
      try {
        const { data, response } = await postUser();
        if (!data) throw new Error('Empty response');
        if (isEntraUser) persistDirectoryUserSummary(data.user);
        return { created: response.status === 201, result: data };
      } catch (caught: unknown) {
        if (isStepUpRequired(caught) && backend.mode === 'fake') {
          await backend.stepUp('/');
          const retry = await postUser();
          if (retry.data) {
            if (isEntraUser) persistDirectoryUserSummary(retry.data.user);
            return { created: retry.response.status === 201, result: retry.data };
          }
        }
        if (isEntraUser) return buildDirectoryFallbackOutcome(input, scope);
        throw caught;
      }
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useReissueInvitation(scope: IdentityScope) {
  const client = useApiClient();
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (id: string): Promise<UserInvitation> => {
      const { data } =
        scope.kind === 'network'
          ? await client.POST('/api/v1/network/identity/users/{id}/invitations', {
              params: { path: { id } },
            })
          : await client.POST('/api/v1/tenants/{tenantId}/identity/users/{id}/invitations', {
              params: { path: { tenantId: scope.tenantId, id } },
            });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ReasonedInput {
  readonly id: string;
  readonly changeReason: ChangeReasonInput;
}

export function useDisableUser() {
  const client = useApiClient();
  const invalidate = useInvalidateScope({ kind: 'network' });
  return useMutation({
    mutationFn: async ({ id, changeReason }: ReasonedInput): Promise<UserSummary> => {
      const { data } = await client.POST('/api/v1/network/identity/users/{id}/disable', {
        params: { path: { id } },
        body: { changeReason },
      });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface LockInput extends ReasonedInput {
  readonly durationMinutes: number | null;
}

export function useLockUser() {
  const client = useApiClient();
  const invalidate = useInvalidateScope({ kind: 'network' });
  return useMutation({
    mutationFn: async ({ id, changeReason, durationMinutes }: LockInput): Promise<UserLock> => {
      const { data } = await client.POST('/api/v1/network/identity/users/{id}/lock', {
        params: { path: { id } },
        body: { changeReason, durationMinutes },
      });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useUnlockUser() {
  const client = useApiClient();
  const invalidate = useInvalidateScope({ kind: 'network' });
  return useMutation({
    mutationFn: async ({ id, changeReason }: ReasonedInput): Promise<UserLock> => {
      const { data } = await client.POST('/api/v1/network/identity/users/{id}/unlock', {
        params: { path: { id } },
        body: { changeReason },
      });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useRevokeAllSessions() {
  const client = useApiClient();
  const invalidate = useInvalidateScope({ kind: 'network' });
  return useMutation({
    mutationFn: async ({ id, changeReason }: ReasonedInput): Promise<number> => {
      const { data } = await client.POST(
        '/api/v1/network/identity/users/{id}/sessions/revoke-all',
        {
          params: { path: { id } },
          body: { changeReason },
        },
      );
      return Number(data?.revokedCount ?? 0);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useRequestMfaReset() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (input: {
      readonly id: string;
      readonly identityVerificationNote: string;
    }): Promise<MfaResetRequest> => {
      const { data } = await client.POST('/api/v1/network/identity/users/{id}/mfa-reset-requests', {
        params: { path: { id: input.id } },
        body: { identityVerificationNote: input.identityVerificationNote },
      });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    meta: { silent: true },
  });
}

export function useApproveMfaReset() {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (id: string): Promise<MfaResetRequest> => {
      const { data } = await client.POST(
        '/api/v1/network/identity/mfa-reset-requests/{id}/approve',
        {
          params: { path: { id } },
        },
      );
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    meta: { silent: true },
  });
}

export function useCreateTemporaryAccount(scope: IdentityScope) {
  const client = useApiClient();
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async (input: CreateTemporaryAccountInput): Promise<TemporaryAccount> => {
      const { data } =
        scope.kind === 'network'
          ? await client.POST('/api/v1/network/identity/temporary-accounts', { body: input })
          : await client.POST('/api/v1/tenants/{tenantId}/identity/temporary-accounts', {
              params: { path: { tenantId: scope.tenantId } },
              body: input,
            });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface ExtendInput extends ExtendTemporaryAccountInput {
  readonly id: string;
}

export function useExtendTemporaryAccount(scope: IdentityScope) {
  const client = useApiClient();
  const invalidate = useInvalidateScope(scope);
  return useMutation({
    mutationFn: async ({ id, ...body }: ExtendInput): Promise<TemporaryAccount> => {
      const { data } =
        scope.kind === 'network'
          ? await client.POST('/api/v1/network/identity/temporary-accounts/{id}/extend', {
              params: { path: { id } },
              body,
            })
          : await client.POST(
              '/api/v1/tenants/{tenantId}/identity/temporary-accounts/{id}/extend',
              {
                params: { path: { tenantId: scope.tenantId, id } },
                body,
              },
            );
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

const CLIENTS_KEY = ['identity', 'network', 'oidc-clients'] as const;

export function useOidcClients() {
  const client = useApiClient();
  return useQuery({
    queryKey: CLIENTS_KEY,
    queryFn: async (): Promise<OidcClient[]> => {
      const { data } = await client.GET('/api/v1/network/identity/oidc-clients');
      return data ?? [];
    },
    meta: { silent: true },
  });
}

function useInvalidateClients() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
  };
}

export function useCreateOidcClient() {
  const client = useApiClient();
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: async (input: CreateOidcClientInput): Promise<OidcClientSecret> => {
      const { data } = await client.POST('/api/v1/network/identity/oidc-clients', { body: input });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export interface UpdateOidcClientVariables {
  readonly id: string;
  readonly input: UpdateOidcClientInput;
}

export function useUpdateOidcClient() {
  const client = useApiClient();
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: async ({ id, input }: UpdateOidcClientVariables): Promise<OidcClient> => {
      const { data } = await client.PUT('/api/v1/network/identity/oidc-clients/{id}', {
        params: { path: { id } },
        body: input,
      });
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useRotateOidcClientSecret() {
  const client = useApiClient();
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: async ({ id, changeReason }: ReasonedInput): Promise<OidcClientSecret> => {
      const { data } = await client.POST(
        '/api/v1/network/identity/oidc-clients/{id}/rotate-secret',
        {
          params: { path: { id } },
          body: { changeReason },
        },
      );
      if (!data) {
        throw new Error('Empty response');
      }
      return data;
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}
